import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";

// hooks
import useAuth from "hooks/useAuth";
import useShiftsTable from "./hooks/useShiftsTable";

// reusable components
import { SecureTableShell } from "@/components/ui/SecureTableShell";
import EmptyStateCard from "@/components/ui/EmptyStateCard";
import ShiftSearchControls from "./components/ShiftSearchControls";
import ShiftSummary from "./components/ShiftSummary";

// utils
import { getFallbackStateShifts } from "./utils/getFallbackStateShifts";

const Shifts = () => {
  const {
    allShifts,
    shifts: filteredShifts,
    locations,
    searchTerm,
    updateSearchTerm,
    statusFilter,
    updateStatusFilter,
    locationFilter,
    updateLocationFilter,
    shiftColumns,
    renderShiftRow,
    isError,
    isLoading,
    refetch,
  } = useShiftsTable();

  const { isAuthenticated } = useAuth();

  const [rawSearchTerm, setRawSearchTerm] = useState(searchTerm);
  useEffect(() => {
    const timeout = setTimeout(() => updateSearchTerm(rawSearchTerm), 600);
    return () => clearTimeout(timeout);
  }, [rawSearchTerm, updateSearchTerm]);

  const fallback = getFallbackStateShifts({
    isLoading,
    allShifts,
    filteredShifts,
  });

  if (!isAuthenticated) {
    return <Navigate to="/auth/sign-in" replace />;
  }

  if (isError) {
    return (
      <div className="p-6">
        <EmptyStateCard
          icon={
            <div className="w-16 h-16 flex items-center justify-center bg-red-100 rounded-full">
              <span className="text-red-600 font-bold text-lg">!</span>
            </div>
          }
          title="Something went wrong"
          description="We couldn't load your shifts. Please try again later."
          buttonLabel="Retry"
          onAction={refetch}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-3 flex items-center justify-end">
        <span className="rounded-full bg-[#f1f0ff] px-3 py-1 text-xs font-semibold text-[#210EEB]">
          View only
        </span>
      </div>

      <ShiftSummary shifts={allShifts} />

      <ShiftSearchControls
        searchTerm={rawSearchTerm}
        updateSearchTerm={setRawSearchTerm}
        statusFilter={statusFilter}
        updateStatusFilter={updateStatusFilter}
        locationFilter={locationFilter}
        updateLocationFilter={updateLocationFilter}
        locations={locations}
      />

      <SecureTableShell
        data={filteredShifts}
        columns={shiftColumns}
        renderRow={renderShiftRow}
        fallbackState={fallback.state}
        fallbackMessages={{
          noData: {
            title: "No shifts recorded",
            description: "Shifts will appear here once staff begin clocking in.",
            buttonLabel: null,
          },
          noMatch: {
            title: "No matching shifts",
            description: "Try adjusting your search or filters.",
            buttonLabel: null,
          },
        }}
      />
    </div>
  );
};

export default Shifts;
